import { AuthStrategy } from '@/lib/auth/strategy'
import { getSiteURL } from '@/lib/get-site-url'
import { LogLevel } from '@/lib/logger'
import type { ColorScheme, PrimaryColor } from '@/styles/theme/types'

export interface Config {
	site: {
		name: string
		description: string
		colorScheme: ColorScheme
		primaryColor: PrimaryColor
		themeColor: string
		url: string
		version: string
	}
	analyticsDisabled: boolean
	logLevel: keyof typeof LogLevel
	auth: { strategy: keyof typeof AuthStrategy }
	auth0: { domain?: string; clientId?: string }
	frontegg: {
		baseUrl?: string
		clientId?: string
		loginCallbackUrl?: string
	}
	mapbox: { apiKey?: string }
	googleMaps: { apiKey?: string }
	gtm?: { id?: string }
	segment?: { writeKey?: string }
	intercom: { appId?: string }
	fridge: { apiUrl?: string; integrationsApiUrl?: string; superUrl?: string; useSessionHeader?: boolean }
	fullstory: { orgId?: string }
	logrocket: { appId?: string }
	sentry: { dsn?: string }
	metabase?: { metabaseInstanceUrl?: string; jwtProviderUri?: string }
	googleVision: { apiKey?: string }
	notaku?: { widgetId?: string }
	betterstack?: { widgetId?: string }
}

// Railway PR preview environments serve Platform from a generated
// platform-<env>.up.railway.app domain, but Railpack builds do not receive the
// service variables, so committed .env.production values (prod auth, prod API)
// get baked into the bundle. Detect the preview host at runtime and point the
// app at the sibling classic service in the same PR environment instead.
const previewSuperUrl = ((): string | undefined => {
	if (typeof window === 'undefined') {
		return undefined
	}
	const match = /^platform-(?<env>micromart-pr-\d+)\.up\.railway\.app$/.exec(window.location.hostname)
	return match?.groups?.env ? `https://classic-${match.groups.env}.up.railway.app` : undefined
})()

const fridgeApiUrl = previewSuperUrl ? `${previewSuperUrl}/api` : import.meta.env.VITE_FRIDGE_API_URL

export const config = {
	site: {
		name: 'Micromart™ Platform',
		description: 'Manage your business remotely with our powerful yet intuitive platform.',
		colorScheme: 'light',
		themeColor: '#08372e',
		primaryColor: 'grassGreen',
		url: getSiteURL(),
		version: import.meta.env.VITE_SITE_VERSION || '0.0.0',
	},
	analyticsDisabled: import.meta.env.VITE_DISABLE_ANALYTICS === 'true' || Boolean(previewSuperUrl),
	logLevel: (import.meta.env.VITE_LOG_LEVEL as keyof typeof LogLevel) || LogLevel.ALL,
	auth: {
		strategy:
			(previewSuperUrl ? AuthStrategy.CUSTOM : (import.meta.env.VITE_AUTH_STRATEGY as keyof typeof AuthStrategy)) ||
			AuthStrategy.CUSTOM,
	},
	auth0: { domain: import.meta.env.VITE_AUTH0_DOMAIN, clientId: import.meta.env.VITE_AUTH0_CLIENT_ID },
	frontegg: {
		baseUrl: import.meta.env.VITE_FRONTEGG_BASE_URL,
		clientId: import.meta.env.VITE_FRONTEGG_CLIENT_ID,
		loginCallbackUrl: import.meta.env.VITE_FRONTEGG_LOGIN_CALLBACK_URL,
	},
	mapbox: { apiKey: import.meta.env.VITE_MAPBOX_API_KEY },
	googleMaps: { apiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY },
	gtm: { id: import.meta.env.VITE_GOOGLE_TAG_MANAGER_ID },
	segment: { writeKey: import.meta.env.VITE_SEGMENT_WRITE_KEY },
	intercom: { appId: import.meta.env.VITE_INTERCOM_APP_ID },
	fridge: {
		apiUrl: fridgeApiUrl,
		integrationsApiUrl: fridgeApiUrl?.replace('api', 'integrations'),
		superUrl: fridgeApiUrl?.replace(/\/api\/?$/, ''),
		useSessionHeader: import.meta.env.VITE_FRIDGE_USE_SESSION_HEADER === 'true' || Boolean(previewSuperUrl),
	},
	fullstory: { orgId: import.meta.env.VITE_FULLSTORY_ORG_ID },
	logrocket: { appId: import.meta.env.VITE_LOGROCKET_APP_ID },
	sentry: { dsn: import.meta.env.VITE_SENTRY_DSN },
	metabase: {
		metabaseInstanceUrl: import.meta.env.VITE_METABASE_INSTANCE_URL,
		jwtProviderUri: previewSuperUrl
			? `${previewSuperUrl}/api/sessions/metabase`
			: import.meta.env.VITE_METABASE_JWT_PROVIDER_URI,
	},
	googleVision: { apiKey: import.meta.env.VITE_GOOGLE_VISION_API_KEY },
	notaku: { widgetId: import.meta.env.VITE_NOTAKU_WIDGET_ID },
	betterstack: {
		widgetId: import.meta.env.VITE_BETTERSTACK_WIDGET_ID,
	},
} satisfies Config
